import React from 'react';
import SignatureProviderImpl from 'signer-app/signature-modal/signature-modal-context/signature-provider-impl';
import { setCsrfToken } from 'js/sign-components/common/hs-fetch';
import { constructStore } from 'hellospa/redux/store';
import { buildWebAppClient } from 'hello-react/web-app-client/build-web-app-client';
import { hasFeatureFlagFactory } from 'js/sign-components/common/check-feature-flag';
import { useOnMount } from 'js/sign-components/common/hooks';
import { setPublishableKey } from 'hellospa/common/utils/stripe-helper';
import type Application from 'hellosign/detached-application';
import type { HSUser } from 'hellospa/common/hs-user';
import type { FeatureFlags } from 'js/sign-components/common/feature-flags';
import type { SiteCode } from 'js/sign-components/generated/types/WebApp';
import type { TourName } from 'js/sign-components/common/TourName';
import type { SigData } from 'signer-app/context/signer-app-client/signature';
import { HelloSPA } from './hellospa-app';
import { helloSpaRoutes } from './hellospa-routes';
import type { GlobalPageProps } from './slices/page-props';
import type { DropboxIntegrationConfirmation } from './components/dropbox-integration-confirmation/dropbox-integration-confirmation';

type Props = {
  app: Application;
  csrfToken: string;
  user?: HSUser;
  featureFlags?: FeatureFlags;
  siteCode: SiteCode;
  isDropbox: boolean;
  isEmbedded: boolean;
  termsURL: string;
  privacyURL: string;
  stripePublishableKey?: string;
  signature?: SigData;
  preloadedTsmGroupKey: string;
  addBackground?: boolean;
  completedTours?: Record<string, TourName>;
  pageKey: GlobalPageProps['pageKey'];
  pageProps: any;
  locale: string;
  dropboxIntegrationConfirmation?: DropboxIntegrationConfirmation | null;
};

/**
 * <Root is the top component in the tree, and the one that configures all the
 * common tools and contexts. It renders `<HelloSPA` and passes it the routes.
 *
 * # Storybook
 * At the moment our Storybook automatically wraps every story in a component
 * that acts like <HelloSPA. I'd like to get this inverted and give everyone a
 * batteries-included `<HelloSPAStory>` whose props should be:
 * `React.Component<Partial<HelloSPAProps>>`
 *
 * # Testing
 * Our tests also share the same wrapper that the Storybook decorator uses.
 *
 * @AppExplorer https://miro.com/app/board/uXjVPXskloA=/?moveToWidget=3458764535231372009&cot=14
 */
export default function Root(props: Props) {
  setCsrfToken(props.csrfToken);
  /**
   * The original intention of layoutContext was to provide specifically the
   * data that our layout needs, that way each individual page didn't have to
   * manage that. They can juse use `<DefaultLayout>` and move along.
   *
   * I'm going to end up disassembling this context a piece at a time.
   * @deprecated
   */
  const layoutContext = React.useMemo(
    () => ({
      /**
       * Use the useHSUser() hook instead of layoutContext.
       *
       * @AppExplorer https://miro.com/app/board/uXjVPXskloA=/?moveToWidget=3458764535231624233&cot=14
       */
      user: props.user,
      /**
       * We're planning to move away from page props, but until that's complete
       * you can use `usePageProps()`
       *
       * @AppExplorer https://miro.com/app/board/uXjVPXskloA=/?moveToWidget=3458764535231624815&cot=14
       */
      pageProps: props.pageProps,
      /**
       * getCSRFToken() can be called from an AppAction/WebAppClient. There's no
       * need to pass it around components.
       */
      csrfToken: props.csrfToken,
      /**
       * useFeatureFlag('flag-name')
       */
      featureFlags: props.featureFlags,

      app: props.app,
      stripePublishableKey: props.stripePublishableKey,
      signature: props.signature,
      isDropbox: props.isDropbox,
      isEmbedded: props.isEmbedded,
      siteCode: props.siteCode,
      termsURL: props.termsURL,
      privacyURL: props.privacyURL,
      sessionInfo: props.pageProps.sessionInfo,
      preloadedTsmGroupKey: props.preloadedTsmGroupKey,
      isSpa: true,
    }),
    [props],
  );
  const appActions = buildWebAppClient(props.preloadedTsmGroupKey);
  const featureFlags = props.featureFlags || {};

  const store = constructStore(() => ({
    appActions,
    featureFlags,
    hasFeatureFlag: hasFeatureFlagFactory(featureFlags),
  }));

  const hsUser = props.user;

  useOnMount(() => {
    if (props.stripePublishableKey) {
      setPublishableKey(props.stripePublishableKey);
    }
  });

  let routes = helloSpaRoutes;
  if (hsUser) {
    routes = (
      <SignatureProviderImpl
        firstName={hsUser.settings?.firstName}
        lastName={hsUser.settings?.lastName}
        primarySignatureGuid={hsUser.primarySignatureGuid}
        signature={props.signature}
        isEmbedded={props.isEmbedded}
        isDropbox={props.isDropbox}
        hideSpinner
      >
        {routes}
      </SignatureProviderImpl>
    );
  }

  return (
    <HelloSPA
      store={store}
      siteCode={props.siteCode}
      appActions={appActions}
      csrfToken={props.csrfToken}
      featureFlags={featureFlags}
      preloadedTsmGroupKey={props.preloadedTsmGroupKey}
      // @ts-expect-error layoutContext shape doesn't fully match LayoutContextShape yet
      layoutContext={layoutContext}
      // @ts-expect-error hsUser may be undefined for unauthenticated routes
      hsUser={hsUser}
      completedTours={Object.values(props.completedTours || {})}
      addBackground={props.addBackground || false}
      dropboxIntegrationConfirmation={props.dropboxIntegrationConfirmation}
      pageProps={{
        pageKey: props.pageKey,
        props: props.pageProps,
      }}
    >
      {routes}
    </HelloSPA>
  );
}
